import React, { useEffect, useState } from "react"
import { Building2, Download, FileText, LogOut, Receipt } from "lucide-react"
import { useAuth } from "src/contexts/AuthContext"
import { fetchPortalBookings, fetchPortalBooking } from "src/features/portal/api/portalApi"

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0)

const STATUS_STYLES = {
  paid: "bg-green-100 text-green-700",
  partially_paid: "bg-blue-100 text-blue-700",
  due: "bg-amber-100 text-amber-700",
  overdue: "bg-red-100 text-red-700",
  void: "bg-slate-100 text-slate-400",
}

export default function PortalHomePage() {
  const [booking, setBooking] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchPortalBookings()
      .then((list) => {
        if (!list.length) { setLoading(false); return }
        return fetchPortalBooking(list[0].id).then(setBooking).finally(() => setLoading(false))
      })
      .catch((e) => { setError(e); setLoading(false) })
  }, [])

  if (loading) return <Shell><div className="py-16 text-center text-sm text-slate-400">Loading your home…</div></Shell>
  if (error) return <Shell><div className="py-16 text-center text-sm text-red-500">Could not load your booking.</div></Shell>
  if (!booking) return <Shell><div className="py-16 text-center text-sm text-slate-400">No booking found on your account yet.</div></Shell>

  return (
    <Shell>
      {/* Unit hero */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-xs text-slate-400">My home</div>
        <div className="mt-0.5 text-lg font-semibold text-slate-900">{booking.project?.name} · Unit {booking.unit?.unit_number}</div>
        <div className="text-sm text-slate-500">
          {[booking.unit?.configuration, booking.unit?.floor != null ? `Floor ${booking.unit.floor}` : null].filter(Boolean).join(" · ") || "—"}
        </div>
        <div className="mt-3">
          <div className="mb-1 flex justify-between text-[11px] text-slate-500">
            <span>Construction-linked progress</span><span>{booking.percent_paid}% paid</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-green-500" style={{ width: `${Math.min(booking.percent_paid, 100)}%` }} />
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric label="Agreement value" value={inr(booking.total_value)} />
        <Metric label="Paid so far" value={inr(booking.amount_received)} tone="text-green-600" />
        <Metric label="Balance" value={inr(booking.balance_due)} />
        <Metric label={booking.next_due ? `Next due · ${fmtDate(booking.next_due.due_on)}` : "Next due"} value={booking.next_due ? inr(booking.next_due.amount) : "—"} tone="text-amber-600" />
      </div>

      {/* Schedule */}
      <Section title="Payment schedule">
        <div className="flex flex-col gap-1.5">
          {(booking.schedule || []).map((i) => (
            <div key={i.id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm">
              <span className="font-medium text-slate-800">{i.name}</span>
              <span className="text-xs text-slate-500">{i.due_on ? fmtDate(i.due_on) : "—"}</span>
              <span className="text-right text-slate-700">{inr(i.amount)}</span>
              <span className={`justify-self-end rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLES[i.status] || "bg-slate-100 text-slate-500"}`}>{i.status}</span>
            </div>
          ))}
          {(booking.schedule || []).length === 0 && <div className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-xs text-slate-400">No schedule yet</div>}
        </div>
      </Section>

      {/* Payments & receipts */}
      <Section title="Payments & receipts">
        <div className="flex flex-col gap-1.5">
          {(booking.payments || []).map((p) => (
            <div key={p.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm">
              <div className="min-w-0">
                <div className="font-medium text-slate-800">{inr(p.amount)}</div>
                <div className="truncate text-[11px] text-slate-500">
                  {[p.mode, p.reference_no].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              <span className="text-xs text-slate-500">{p.paid_on ? fmtDate(p.paid_on) : "—"}</span>
              <span className={`justify-self-end rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLES[p.status] || "bg-slate-100 text-slate-500"}`}>{p.status}</span>
            </div>
          ))}
          {(booking.payments || []).length === 0 && (
            <div className="flex items-center gap-2 rounded-xl border border-dashed border-slate-200 px-4 py-3 text-xs text-slate-400">
              <Receipt className="h-4 w-4" /> Your payment receipts will appear here
            </div>
          )}
        </div>
      </Section>

      {/* Documents */}
      <Section title="Documents">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(booking.documents || []).map((d) => (
            <div key={d.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm">
              <FileText className="h-4 w-4 text-slate-400" />
              <span className="flex-1 truncate">{d.title || d.file_name}</span>
              <Download className="h-4 w-4 text-blue-500" />
            </div>
          ))}
          {(booking.documents || []).length === 0 && (
            <div className="flex items-center gap-2 rounded-xl border border-dashed border-slate-200 px-4 py-3 text-xs text-slate-400">
              <Receipt className="h-4 w-4" /> Documents will appear here as they're issued
            </div>
          )}
        </div>
      </Section>
    </Shell>
  )
}

function Shell({ children }) {
  const { currentUser, organization, logout } = useAuth()
  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-5 flex items-center justify-between border-b border-slate-200 pb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><Building2 className="h-5 w-5" /></span>
          <div className="leading-tight">
            <div className="font-semibold text-slate-900">My home</div>
            {organization?.name && <div className="text-[11px] text-slate-400">{organization.name}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {currentUser?.name && <span className="hidden text-xs text-slate-500 sm:inline">{currentUser.name}</span>}
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </div>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  )
}

function Metric({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="rounded-xl bg-slate-50 px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-semibold ${tone}`}>{value}</div>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div>
      <div className="mb-2 text-sm font-medium text-slate-800">{title}</div>
      {children}
    </div>
  )
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) }
  catch { return iso }
}
